/**
 * Knowledge Retriever for Farm Manager AI
 * 
 * Retrieves relevant documentation sections based on query context.
 * Uses simple text matching for MVP - can be enhanced with embeddings later.
 */

import { DEFAULT_FARM_MANAGER_SETTINGS } from "../../database/farmManagerSettings";

/**
 * Simple keyword-based retrieval from knowledge documents
 * @param {string} query - User query or conversation context
 * @param {Object} options - Retrieval options
 * @param {number} options.maxResults - Maximum number of sections to return (default: 3)
 * @param {Object} options.enabledSections - Object mapping section keys to enabled status
 * @returns {Promise<Array<string>>} Array of relevant knowledge sections
 */
export async function retrieveRelevantKnowledge(query = "", options = {}) {
        const { maxResults = 3, enabledSections = {} } = options;

        if (!query || typeof query !== "string" || query.trim().length === 0) {
                return [];
        }

        // Normalize query to lowercase for matching
        const normalizedQuery = query.toLowerCase();

        // Define knowledge sections with keywords
        const knowledgeSections = [
                {
                        keywords: ["health", "score", "suspicious", "sick", "illness", "disease", "fever", "lameness", "symptoms"],
                        section: "healthIssues",
                        priority: 1,
                },
                {
                        keywords: ["treatment", "medication", "drug", "vaccine", "therapy", "dosage", "protocol"],
                        section: "treatmentProtocols",
                        priority: 1,
                },
                {
                        keywords: ["calf", "calving", "colostrum", "weaning", "lifecycle", "newborn", "birth", "pregnancy"],
                        section: "calfLifecycle",
                        priority: 1,
                },
                {
                        keywords: ["bionode", "device", "sensor", "connectivity", "offline", "troubleshoot", "diagnostic"],
                        section: "bionodeTroubleshooting",
                        priority: 1,
                },
                {
                        keywords: ["best practice", "guide", "how to", "manage", "operational", "daily", "checklist"],
                        section: "operationalGuidance",
                        priority: 2,
                },
                {
                        keywords: ["feature", "capability", "tool", "function", "what can", "available"],
                        section: "featureCapabilities",
                        priority: 3,
                },
                {
                        keywords: ["livestock", "animal", "herd", "group", "activity", "monitoring"],
                        section: "livestockManagement",
                        priority: 2,
                },
                {
                        keywords: [
                                "production",
                                "calving",
                                "colostrum",
                                "vaccination",
                                "lifecycle",
                                "weight",
                                "adg",
                                "weaning",
                                "csv",
                                "import",
                        ],
                        section: "productionData",
                        priority: 2,
                },
        ];

        // Score sections based on keyword matches
        const scoredSections = knowledgeSections.map((section) => {
                let score = 0;
                let matchCount = 0;

                section.keywords.forEach((keyword) => {
                        if (normalizedQuery.includes(keyword)) {
                                score += section.priority * 2;
                                matchCount++;
                        }
                });

                // Boost score if multiple keywords match
                if (matchCount > 1) {
                        score += matchCount * section.priority;
                }

                return {
                        ...section,
                        score,
                        matchCount,
                };
        });

        // Filter and sort by score, and check if sections are enabled
        const relevantSections = scoredSections
                .filter((section) => {
                        // Only include if score > 0 and section is enabled (default true if not specified)
                        return section.score > 0 && enabledSections[section.section] !== false;
                })
                .sort((a, b) => b.score - a.score)
                .slice(0, maxResults)
                .map((section) => section.section);

        return relevantSections;
}

/**
 * Retrieves knowledge sections based on conversation context
 * @param {Array} conversationHistory - Recent conversation messages
 * @param {Object} options - Retrieval options
 * @returns {Promise<Array<string>>} Array of relevant section keys
 */
export async function retrieveKnowledgeFromConversation(conversationHistory = [], options = {}) {
        if (!conversationHistory || conversationHistory.length === 0) {
                return [];
        }

        // Extract text from recent messages (last 3-5 messages)
        const recentMessages = conversationHistory.slice(-5);
        const combinedText = recentMessages
                .map((msg) => (typeof msg === "object" ? msg.content || msg.text || "" : String(msg)))
                .filter((text) => text && text.trim().length > 0)
                .join(" ")
                .toLowerCase();

        if (combinedText.length === 0) {
                return [];
        }

        return retrieveRelevantKnowledge(combinedText, options);
}

/**
 * Gets default knowledge sections for general context
 * @param {Object} knowledgeBaseSettings - Knowledge base settings from user config
 * @returns {Array<string>} Array of default section keys to always include
 */
export function getDefaultKnowledgeSections(knowledgeBaseSettings = null) {
        const settings = knowledgeBaseSettings || DEFAULT_FARM_MANAGER_SETTINGS.knowledgeBase;
        return settings.defaultSections || ["featureCapabilities", "operationalGuidance"];
}

